import React from 'react'
// libs
import moment from 'moment'
import type { Moment } from 'moment'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// components
import Label from 'components/FormElements/DatePicker/components/Label'
import ClearButton from 'components/FormElements/DatePicker/components/ClearButton'
import CustomToolbar, { DATE_SPECIAL_TYPES } from 'components/FormElements/DatePicker/components/CustomToolbar'
// types
import { IOption } from 'components/FormElements/Select'
import { TextFieldVariants } from '@mui/material'
// eslint-disable-next-line no-restricted-imports
import { ExportedDatePickerToolbarProps } from '@mui/x-date-pickers/DatePicker/DatePickerToolbar'
// utils
import { trimTz } from 'utils/date'

export const formatDateValue = (value: string | IOption) => (typeof value === 'string' ? moment(value) : null)

const DIALOG_PROPS = {
  disableRestoreFocus: true
}

export const useDatePicker = ({
  value,
  label,
  error,
  required,
  onChange,
  shouldTrimTz
}: {
  value: string | IOption
  label: string
  error: boolean
  required: boolean
  onChange: (value: string | IOption) => void
  shouldTrimTz?: boolean
}) => {
  const { isRisk, isRiskTemplate } = React.useContext(RiskTemplateAPIContext)

  const [menuOpen, setMenuOpen] = React.useState(false)
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [date, setDate] = React.useState<Moment | IOption | null>(() =>
    value && typeof value === 'string' ? moment(value) : null
  )

  const showAsterisk = (isRisk || isRiskTemplate) && required

  const handleOnChange = (value: Moment | IOption) => {
    if (moment.isMoment(value)) {
      setDate(value)

      if (value.isValid()) {
        onChange(shouldTrimTz ? trimTz(value) : value)
      }
    } else {
      onChange(value)
    }
  }

  const onClose = () => {
    if (!menuOpen) {
      setPickerOpen(false)
    }
  }

  React.useEffect(() => {
    if (!date && value && typeof value === 'string') {
      setDate(moment(value))
    }
  }, [date, value])

  const props = {
    onClose,
    onChange: handleOnChange,
    open: pickerOpen,
    value: date,
    onOpen: () => setPickerOpen(true),
    slots: {
      toolbar: isRiskTemplate ? CustomToolbar : null
    },
    slotProps: {
      toolbar: {
        value,
        menuOpen,
        setMenuOpen,
        onChange: handleOnChange
      } as ExportedDatePickerToolbarProps,
      openPickerButton: {
        sx: {
          backgroundColor: 'transparent',
          '&:hover': { backgroundColor: 'transparent' },
          '&:active': { backgroundColor: 'transparent' },
          '& .MuiTouchRipple-root': { display: 'none' }
        }
      },
      dialog: DIALOG_PROPS,
      textField: {
        error,
        label: <Label text={label} showAsterisk={showAsterisk} />,
        variant: 'standard' as TextFieldVariants,
        ...(DATE_SPECIAL_TYPES.includes((value as IOption)?.value) && {
          InputProps: {
            endAdornment: <ClearButton onChange={() => handleOnChange(null)} />
          },
          inputProps: {
            value: (value as IOption).label
          }
        })
      }
    }
  }

  return { props }
}
